"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { SectionHeading } from "@/components/ui/section-heading"
import { Check, Star } from "lucide-react"
import Link from "next/link"
import { useRouter } from 'next/navigation';
import { useAuth } from '@/hooks/use-auth';

// Updated plans array with annual prices (calculated as monthly * 12 * 0.8)
interface Plan {
  id: number
  name: string
  monthlyPrice: string
  annualPrice: string // Added annual price
  pricePeriod: string // Base period (e.g., /month)
  description: string
  features: string[]
  ctaText: string
  ctaLink: string
  isPopular?: boolean
  isEnterprise?: boolean
  stripePriceId?: string
}

const plans: Plan[] = [
  {
    id: 1,
    name: "Free",
    monthlyPrice: "$0",
    annualPrice: "$0",
    pricePeriod: "",
    description: "Free tier for basic needs",
    features: [
      "3 Article Credits (One-time)",
      "1 Project Limit",
      "1 User",
      "All Core Generation Features",
      "Community Support",
    ],
    ctaText: "Get Started",
    ctaLink: "/signup/dashboard",
  },
  {
    id: 2,
    name: "Pro",
    monthlyPrice: "$29",
    annualPrice: "$279", // Adjusted from $278
    pricePeriod: "/month",
    description: "Great for growing businesses",
    features: [
      "10 Article Credits / month",
      "3 Project Limit",
      "1 User",
      "All Core Generation Features",
      "Email Support",
    ],
    ctaText: "Subscribe Now",
    ctaLink: "/subscription/checkout",
    isPopular: true,
  },
  {
    id: 3,
    name: "Business",
    monthlyPrice: "$59",
    annualPrice: "$569", // Adjusted from $566
    pricePeriod: "/month",
    description: "For established businesses",
    features: [
      "25 Article Credits / month",
      "7 Project Limit",
      "3 Users Included",
      "All Core Generation Features",
      "Limited API Access (Optional - TBD)",
      "Priority Email/Chat Support",
    ],
    ctaText: "Subscribe Now",
    ctaLink: "/subscription/checkout",
  },
  {
    id: 5,
    name: "Enterprise",
    monthlyPrice: "Custom",
    annualPrice: "Custom",
    pricePeriod: "",
    description: "For large organizations",
    features: [
      "Custom Article Credits",
      "Unlimited Projects",
      "Custom User Seats",
      "All Core Generation Features",
      "Full API Access",
      "Dedicated Manager",
      "Priority Support",
      "Custom Integrations",
    ],
    ctaText: "Contact Sales",
    ctaLink: "/contact",
    isEnterprise: true,
  },
]

// Featured plans to highlight (Free and Pro)
const featuredPlans = plans.filter(plan => plan.id === 1 || plan.id === 2);
// Additional plans for tab display
const additionalPlans = plans.filter(plan => plan.id === 3 || plan.id === 5);

// Define new minimal plans with subtitle
const minimalPlans = [
  {
    id: 1,
    name: "Free",
    subtitle: "Try it out for free",
    price: "$0",
    annual: null,
    features: [
      "1 Website",
      "3 Articles (one-time)",
      "Multi-language",
      "Email Support",
    ],
    ctaText: "Get Started",
    ctaLink: "/dashboard",
    highlighted: false,
  },
  {
    id: 2,
    name: "Plus",
    subtitle: "For copywriters & individuals",
    price: "$29",
    annual: null,
    features: [
      "3 Websites",
      "10 Articles / month",
      "Multi-language",
      "Email Support"
    ],
    ctaText: "Subscribe",
    highlighted: false,
    stripePriceId: "price_1RSfTrRoqcChBqWLDYRuUFb0",
  },
  {
    id: 3,
    name: "Agency",
    subtitle: "For agencies & teams",
    price: "$79",
    annual: null,
    features: [
      "7 Websites",
      "25 Articles / month",
      "Multi-language",
      "Priority Support"
    ],
    ctaText: "Subscribe",
    highlighted: true,
    stripePriceId: "price_1RSfVSRoqcChBqWLHehlHMON",
  },
];

// Utility to call the backend and start a Stripe subscription
async function createStripeSubscription(priceId: string, planId: number): Promise<{ url?: string; error?: string }> {
  try {
    const res = await fetch('/api/payments/create-subscription', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ priceId, planId }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Unknown error');
    return { url: data.url };
  } catch (e: any) {
    return { error: e.message || 'Failed to create subscription' };
  }
}

export default function PricingSection() {
  // State for Monthly/Yearly toggle
  const [isYearly, setIsYearly] = useState(false)
  // State for showing all plans
  const [showAllPlans, setShowAllPlans] = useState(false)
  const router = useRouter();
  const { user, isLoading } = useAuth();
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubscribe = async (plan: any) => {
    setError(null);
    if (!user && !isLoading) {
      router.push('/login?redirectUrl=/pricing');
      return;
    }
    setLoadingPlan(plan.name);
    const priceId = plan.stripePriceId; // Ensure this is set in your plan data
    const { url, error: apiError } = await createStripeSubscription(priceId, plan.id);
    setLoadingPlan(null);
    if (apiError) {
      setError(apiError);
      return;
    }
    if (url) {
      window.location.href = url;
    }
  };

  return (
    <section id="pricing" className="w-full py-8 sm:py-12 md:py-16 lg:py-20 bg-background">
      <div className="container px-4 md:px-6 max-w-4xl mx-auto">
        <SectionHeading
          title="Simple, Transparent Pricing"
          description="Choose the plan that's right for your content needs."
          className="mb-8"
        />
        {/* Minimal, always visible pricing cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 mt-8 sm:mt-10 md:mt-12">
          {minimalPlans.map((plan) => (
            <div
              key={plan.id}
              className={`flex flex-col justify-between rounded-2xl border bg-card shadow-lg p-4 sm:p-6 transition-all duration-200 hover:-translate-y-1 hover:shadow-2xl ${plan.highlighted ? "border-primary border-2" : "border-border"}`}
              style={{ minHeight: undefined }}
            >
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-card-foreground mb-1">{plan.name}</h3>
                <div className="text-xs sm:text-sm text-muted-foreground mb-4">{plan.subtitle}</div>
                <div className="text-3xl sm:text-4xl font-extrabold text-card-foreground mb-1">{plan.price}</div>
                {plan.annual && (
                  <div className="text-xs text-muted-foreground mb-2">Billed {plan.annual} annually</div>
                )}
                <hr className="my-3 sm:my-4 border-muted-foreground/20" />
                <ul className="mb-4 sm:mb-6 space-y-2 sm:space-y-3">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm sm:text-base font-semibold text-card-foreground">
                      <Check className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
              {plan.name === 'Free' ? (
                <Button
                  className="w-full mt-auto text-sm sm:text-base font-semibold py-2 sm:py-2.5"
                  onClick={() => router.push('/dashboard')}
                  disabled={isLoading}
                >
                  {plan.ctaText}
                </Button>
              ) : (
                <Button
                  className="w-full mt-auto text-sm sm:text-base font-semibold py-2 sm:py-2.5"
                  onClick={() => handleSubscribe(plan)}
                  disabled={loadingPlan === plan.name || isLoading}
                >
                  {loadingPlan === plan.name ? 'Redirecting...' : plan.ctaText}
              </Button>
              )}
              {error && loadingPlan === null && (
                <div className="text-red-600 mt-2 text-sm">{error}</div>
              )}
            </div>
          ))}
        </div>
        {/* Enterprise sentence below cards */}
        <div className="text-center mt-4 text-base text-muted-foreground">
          <strong>Enterprise plan:</strong> Need custom amount of projects and articles? <Link href="/contact" className="underline text-primary">Let's Talk.</Link>
        </div>
      </div>
    </section>
  )
}

